<template>
  <div class="container mt-5">
    <div class="row justify-content-center">
      <div class="col-md-8 col-lg-6">
        <div class="card text-center shadow-lg border-0 claim-card">
          <div class="card-body py-5 px-sm-5">
            <img src="@/assets/logo.png" alt="Logo" class="mb-4 logo-img" style="width: 150px;" />
            
            <!-- Provisioning Loader / Progress View -->
            <div v-if="loading && !claimedAccount.email">
              <h3 class="fw-bold mb-3 text-dark">Provisioning Your Demo Account</h3>
              <p class="text-muted small mb-4">Please wait while we build your secure workspace. This normally takes about 10-15 seconds.</p>
              
              <!-- Progress Bar -->
              <div class="progress mb-4 rounded-pill shadow-sm" style="height: 12px;">
                <div class="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                     role="progressbar" 
                     :style="{ width: progressPercentage + '%' }" 
                     :aria-valuenow="progressPercentage" 
                     aria-valuemin="0" 
                     aria-valuemax="100">
                </div>
              </div>
              
              <!-- Steps Checklist -->
              <div class="text-start mt-4 px-2">
                <div v-for="step in stepsList" :key="step.id" class="step-item d-flex align-items-center mb-3">
                  <div class="step-icon-container me-3 d-flex align-items-center justify-content-center">
                    <span v-if="progressStep > step.id" class="step-icon success-icon bg-success-soft text-success rounded-circle">
                      <i class="bi bi-check-lg fs-5"></i>
                    </span>
                    <span v-else-if="progressStep === step.id" class="step-icon active-icon spinner-border spinner-border-sm text-primary" role="status"></span>
                    <span v-else class="step-icon pending-icon bg-light text-muted rounded-circle border"></span>
                  </div>
                  <div class="step-label" :class="{ 'fw-bold text-dark': progressStep === step.id, 'text-success-muted': progressStep > step.id, 'text-muted': progressStep < step.id }">
                    {{ step.name }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Pre-Claim State -->
            <div v-if="!loading && !claimedAccount.email">
              <h2 class="card-title fw-bold text-dark">Claim Your Account</h2>
              <p class="card-text">for session: <strong class="text-primary">{{ sessionName }}</strong></p>
              <p class="card-text text-muted mb-4">Click the button below to generate your temporary Google Workspace account. This may take up to 10 seconds.</p>
              
              <button @click="claimAccount" class="btn btn-primary btn-lg px-5 rounded-pill shadow-sm claim-btn" :disabled="loading">
                Claim My Account
              </button>

              <div v-if="error" class="alert alert-danger mt-4" role="alert">
                {{ error }}
              </div>
            </div>

            <!-- Ready State -->
            <div v-if="!loading && claimedAccount.email">
              <div class="mb-4">
                <h2 class="card-title text-success fw-bold animate-ready"><i class="bi bi-check-circle-fill me-2"></i>Account Ready!</h2>
                <p class="card-text text-muted">Your temporary demo environment has been successfully provisioned.</p>
              </div>
              
              <div class="p-4 bg-light border rounded-3 mb-4 shadow-sm credentials-box">
                <div class="input-group mb-3">
                  <span class="input-group-text bg-white border-end-0 text-muted fw-bold" style="width: 105px;">Username</span>
                  <input type="text" class="form-control bg-white border-start-0 ps-0" :value="claimedAccount.email" readonly>
                  <button class="btn btn-outline-secondary bg-white copy-btn" @click="copyToClipboard(claimedAccount.email, 'username')">
                    <i :class="usernameIcon"></i>
                  </button>
                </div>
                
                <div class="input-group mb-0">
                  <span class="input-group-text bg-white border-end-0 text-muted fw-bold" style="width: 105px;">Password</span>
                  <input type="text" class="form-control bg-white border-start-0 ps-0" :value="claimedAccount.password" readonly>
                  <button class="btn btn-outline-secondary bg-white copy-btn" @click="copyToClipboard(claimedAccount.password, 'password')">
                    <i :class="passwordIcon"></i>
                  </button>
                </div>
              </div>

              <!-- Backup codes for security demo -->
              <div v-if="claimedAccount.backupCodes && claimedAccount.backupCodes.length > 0" class="p-4 bg-warning-soft border border-warning rounded-3 mb-4 shadow-sm text-start">
                <h6 class="fw-bold text-warning-dark mb-2"><i class="bi bi-shield-lock-fill me-2"></i>2SV Recovery Codes:</h6>
                <p class="text-muted small mb-3">Since 2-Step Verification is active for the Security Demo, please use one of these codes if prompted during sign-in.</p>
                <div class="row g-2">
                  <div v-for="(code, idx) in claimedAccount.backupCodes" :key="idx" class="col-6">
                    <div class="p-2 bg-white border rounded text-center font-monospace small position-relative">
                      {{ code }}
                      <button class="btn btn-sm btn-link p-0 position-absolute end-0 top-50 translate-middle-y me-2" @click="copyToClipboard(code, 'code-' + idx)" title="Copy Code">
                        <i :class="codeCopiedStates[idx] ? 'bi bi-check-lg text-success' : 'bi bi-clipboard text-muted'"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div class="text-start mt-4 mb-4">
                <h6 class="mb-3 fw-bold text-secondary text-uppercase tracking-wide" style="letter-spacing: 1px; font-size: 0.85rem;">How to sign in safely:</h6>
                
                <div class="d-flex align-items-start mb-3 bg-white p-3 rounded border signin-step">
                  <i class="bi bi-laptop text-primary fs-3 me-3 mt-1"></i>
                  <div>
                    <strong class="d-block mb-1 text-dark">On a PC or Mac</strong>
                    <p class="text-muted small mb-0">To avoid mixing data with your personal account, we strongly recommend using a <a href="https://support.google.com/chrome/answer/2364824" target="_blank" class="text-decoration-none fw-semibold">new Chrome Profile</a> or an Incognito window.</p>
                  </div>
                </div>

                <div class="d-flex align-items-start mb-3 bg-white p-3 rounded border signin-step">
                  <i class="bi bi-pc-display text-primary fs-3 me-3 mt-1"></i>
                  <div>
                    <strong class="d-block mb-1 text-dark">On a Chromebook</strong>
                    <p class="text-muted small mb-0">From the main sign-in screen, enter the demo credentials provided above. If you are currently signed into another session, please sign out first.</p>
                  </div>
                </div>
              </div>

              <div class="alert alert-warning py-3 mt-4 text-start d-flex align-items-center rounded-3" role="alert">
                <i class="bi bi-exclamation-triangle-fill fs-4 text-warning me-3"></i>
                <small class="text-dark"><strong>Friendly reminder:</strong> This is a temporary, shared environment. Do not upload or store personal or sensitive information.</small>
              </div>
              
              <a href="https://accounts.google.com/signin" target="_blank" class="btn btn-primary btn-lg mt-3 w-100 rounded-pill shadow-sm login-btn">
                Go to Google Sign-In <i class="bi bi-box-arrow-up-right ms-2"></i>
              </a>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';

export default {
  setup() {
    const route = useRoute();
    const sessionName = route.params.sessionName;
    
    const loading = ref(false);
    const error = ref(null);
    const claimedAccount = ref({});
    const usernameCopied = ref(false);
    const passwordCopied = ref(false);
    const codeCopiedStates = ref({});

    // Streaming state
    const progressStep = ref(0);
    const progressMessage = ref('');
    const isSecurityDemo = ref(false);

    const stepsList = computed(() => {
      const baseSteps = [
        { id: 1, name: 'Verifying session capacity' },
        { id: 2, name: 'Preparing Organizational Unit' },
        { id: 3, name: 'Creating Google Workspace account' },
        { id: 4, name: 'Assigning Workspace & Gemini licenses' },
        { id: 5, name: 'Joining Google Groups' },
        { id: 6, name: 'Setting up Drive & Classroom resources' },
      ];
      if (isSecurityDemo.value) {
        baseSteps.push({ id: 7, name: 'Generating secure 2SV backup codes' });
      }
      return baseSteps;
    });

    const progressPercentage = computed(() => {
      const total = stepsList.value.length;
      if (progressStep.value === 0) return 0;
      if (progressStep.value > total) return 100;
      
      const map = {
        1: 10,
        2: 25,
        3: 45,
        4: 60,
        5: 75,
        6: 90,
        7: 95
      };
      return map[progressStep.value] || Math.round((progressStep.value / total) * 100);
    });

    const usernameIcon = computed(() => usernameCopied.value ? 'bi bi-clipboard-check-fill text-success' : 'bi bi-clipboard');
    const passwordIcon = computed(() => passwordCopied.value ? 'bi bi-clipboard-check-fill text-success' : 'bi bi-clipboard');

    onMounted(() => {
      const storedAccount = localStorage.getItem(`claimedAccount_${sessionName}`);
      if (storedAccount) {
        claimedAccount.value = JSON.parse(storedAccount);
      }
    });

    const claimAccount = async () => {
      loading.value = true;
      error.value = null;
      progressStep.value = 0;
      progressMessage.value = 'Connecting to provisioning service...';
      isSecurityDemo.value = false;

      // Generate a unique progressId
      const progressId = Math.random().toString(36).substring(2) + Date.now().toString(36);

      let unsubscribe = null;
      try {
        const progressDocRef = doc(db, 'sessionClaimsProgress', progressId);
        unsubscribe = onSnapshot(progressDocRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.step !== undefined) {
              progressStep.value = data.step;
            }
            if (data.message !== undefined) {
              progressMessage.value = data.message;
            }
            if (data.isSecurityDemo !== undefined) {
              isSecurityDemo.value = data.isSecurityDemo;
            }
          }
        }, (err) => {
          console.error('Firestore snapshot listener error:', err);
        });

        const url = `https://us-central1-demotool-e1a5e.cloudfunctions.net/claimDynamicAccount?sessionName=${sessionName}&progressId=${progressId}`;
        const response = await fetch(url);

        if (!response.ok) {
          const data = await response.json();
          error.value = data.error || 'An error occurred while provisioning your account.';
          loading.value = false;
          if (unsubscribe) unsubscribe();
          return;
        }

        const data = await response.json();
        claimedAccount.value = {
          email: data.email,
          password: data.password,
          backupCodes: data.backupCodes
        };
        localStorage.setItem(`claimedAccount_${sessionName}`, JSON.stringify(claimedAccount.value));
        progressStep.value = stepsList.value.length + 1; // Completed

      } catch (e) {
        console.error('Connection error:', e);
        error.value = 'Failed to connect to the server. Please try again.';
      } finally {
        if (unsubscribe) {
          unsubscribe();
        }
        loading.value = false;
      }
    };

    const copyToClipboard = (text, type) => {
      navigator.clipboard.writeText(text);
      if (type === 'username') {
        usernameCopied.value = true;
        setTimeout(() => (usernameCopied.value = false), 2000);
      } else if (type === 'password') {
        passwordCopied.value = true;
        setTimeout(() => (passwordCopied.value = false), 2000);
      } else if (type.startsWith('code-')) {
        const idx = type.split('-')[1];
        codeCopiedStates.value[idx] = true;
        setTimeout(() => (codeCopiedStates.value[idx] = false), 2000);
      }
    };

    return {
      sessionName,
      loading,
      error,
      claimedAccount,
      claimAccount,
      copyToClipboard,
      usernameIcon,
      passwordIcon,
      progressStep,
      progressMessage,
      stepsList,
      progressPercentage,
      codeCopiedStates,
    };
  },
};
</script>

<style scoped>
.claim-card {
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
}

.logo-img {
  transition: transform 0.3s ease;
}

.logo-img:hover {
  transform: scale(1.05);
}

.step-icon-container {
  width: 28px;
  height: 28px;
}

.step-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.success-icon {
  animation: scaleUp 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

.pending-icon {
  background-color: #f1f3f5;
  border: 1px solid #dee2e6 !important;
}

.bg-success-soft {
  background-color: #d1e7dd;
}

.text-success-muted {
  color: #198754;
  opacity: 0.65;
  text-decoration: line-through;
}

.bg-warning-soft {
  background-color: #fff3cd;
}

.text-warning-dark {
  color: #664d03;
}

.credentials-box {
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.credentials-box:hover {
  transform: translateY(-2px);
}

.signin-step {
  transition: all 0.2s ease;
}

.signin-step:hover {
  transform: translateX(4px);
  border-color: #86b7fe !important;
}

@keyframes scaleUp {
  0% {
    transform: scale(0.6);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-ready {
  animation: fadeInDown 0.5s ease;
}

@keyframes fadeInDown {
  0% {
    transform: translateY(-10px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

.claim-btn {
  transition: all 0.2s ease;
}

.claim-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3) !important;
}

.login-btn {
  transition: all 0.2s ease;
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3) !important;
}

.copy-btn {
  transition: all 0.2s ease;
}

.copy-btn:hover {
  background-color: #f8f9fa !important;
}
</style>